import Group, { GroupItem } from './Group'
import { Divider, Flex } from 'theme-ui'
import TextWithOverFlowAndTippyPopup from 'components/TextWithOverFlowAndTippyPopup'
import IpDescription from './IpDescription'
import React, { ReactElement } from 'react'
import { LatencyStatus, notAvailable } from './index'
import { IP } from 'store/api/configurationStatus'
import LoadingPlaceholder from './LoadingPlaceholder'
import useGetColorMode from 'utils/useGetColorMode'
import { TextWithBreakpointVariant } from 'ui/TextWithBreakpointVariant'

export default function ProxyGroup({
  isProxyAuthorized,
  proxyLatency,
  proxyHost,
  proxyIp,
  newIPv4,
  newIPv6,
  originIPv4,
  originIPv6,
  isProxyLatencyLoading,
  isProxyLatencyLoading2,
  isProxyAuthLoading,
  proxiesLoading,
  isNewIpv4Loading,
  isNewIpv6Loading,
}: {
  isProxyAuthorized?: boolean
  proxyLatency: string
  proxyHost: string
  proxyIp: string
  newIPv4?: IP
  newIPv6?: IP
  originIPv4?: IP
  originIPv6?: IP
  isProxyLatencyLoading: boolean
  isProxyLatencyLoading2: boolean
  isProxyAuthLoading: boolean
  proxiesLoading: boolean
  isNewIpv4Loading: boolean
  isNewIpv6Loading: boolean
}): ReactElement {
  const isLoading = isProxyLatencyLoading || isProxyLatencyLoading2
  const { isLightMode } = useGetColorMode()

  return (
    <Group
      title="Proxy"
      badgeText={isProxyAuthorized ? 'Authorized' : 'Not Authorized'}
      isSuccess={isProxyAuthorized}
      isLoading={proxiesLoading || isProxyAuthLoading}
    >
      <GroupItem>
        <TextWithBreakpointVariant variant={['size14Weight600Line138', 'size15Weight700Line138']}>
          Latency
        </TextWithBreakpointVariant>
        <LoadingPlaceholder isLoading={isLoading}>
          <LatencyStatus latency={proxyLatency} />
        </LoadingPlaceholder>
      </GroupItem>
      <Divider sx={{ width: '100%', height: '1px', color: 'lightSkyBlue10' }} />
      <GroupItem>
        <TextWithBreakpointVariant variant={['size14Weight600Line138', 'size15Weight700Line138']}>
          Host
        </TextWithBreakpointVariant>
        <LoadingPlaceholder isLoading={isLoading}>
          <TextWithBreakpointVariant variant={['size12Weight400Line138', 'size15Weight400Line138']}>
            {proxyHost || notAvailable}
          </TextWithBreakpointVariant>
        </LoadingPlaceholder>
      </GroupItem>
      <Divider sx={{ width: '100%', height: '1px', color: 'lightSkyBlue10' }} />
      <GroupItem>
        <TextWithBreakpointVariant
          variant={['size14Weight600Line138', 'size15Weight700Line138']}
          sx={{ display: 'inline-flex', flexShrink: 0, mr: '0.8rem' }}
        >
          IP
        </TextWithBreakpointVariant>
        <LoadingPlaceholder isLoading={isLoading}>
          <TextWithOverFlowAndTippyPopup
            theme={isLightMode ? 'light-org-tooltip' : 'org-tooltip'}
            variant={['size12Weight400Line138', 'size15Weight400Line138']}
            content={proxyIp || notAvailable}
            ariaLabel={proxyIp || notAvailable}
          />
        </LoadingPlaceholder>
      </GroupItem>

      {((!isNewIpv4Loading && newIPv4 && newIPv4?.ip !== originIPv4?.ip) ||
        (!isNewIpv6Loading && newIPv6 && newIPv6?.ip !== originIPv6?.ip)) && (
        <Group sxMainContent={{ backgroundColor: 'roundedTertiary5' }}>
          {newIPv4?.ip !== originIPv4?.ip && (
            <GroupItem sx={{ px: ['0.4rem', '2.4rem'] }}>
              <TextWithBreakpointVariant
                variant={['size14Weight600Line138', 'size15Weight700Line138']}
                sx={{
                  width: 'fit-content',
                  maxWidth: ['7rem', '100%'],
                  textAlign: 'left',
                  flexShrink: 0,
                }}
              >
                New IPv4
              </TextWithBreakpointVariant>
              {newIPv4?.ip ? (
                <Flex
                  sx={{
                    flexDirection: 'column',
                    textAlign: 'right',
                    gap: '0.8rem',
                  }}
                >
                  <TextWithOverFlowAndTippyPopup
                    theme={isLightMode ? 'light-org-tooltip' : 'org-tooltip'}
                    variant={['size12Weight400Line138', 'size15Weight400Line138']}
                    content={newIPv4?.ip}
                    ariaLabel={newIPv4?.ip}
                  />
                  <IpDescription
                    country={newIPv4?.country}
                    city={newIPv4?.city}
                    isp={newIPv4?.org}
                  />
                </Flex>
              ) : (
                <TextWithBreakpointVariant
                  variant={['size12Weight400Line138', 'size15Weight400Line138']}
                >
                  N/A
                </TextWithBreakpointVariant>
              )}
            </GroupItem>
          )}
          {newIPv6?.ip !== originIPv6?.ip && (
            <Flex sx={{ flexDirection: 'column', width: '100%' }}>
              <Divider sx={{ width: '100%', height: '1px', color: 'lightSkyBlue10' }} />
              <GroupItem sx={{ px: ['0.4rem', '2.4rem'] }}>
                <TextWithBreakpointVariant
                  variant={['size14Weight600Line138', 'size15Weight700Line138']}
                  sx={{
                    width: 'fit-content',
                    maxWidth: ['7rem', '100%'],
                    textAlign: 'left',
                    overflowWrap: 'break-word',
                    mr: '0.4rem',
                    flexShrink: 0,
                  }}
                >
                  New IPv6
                </TextWithBreakpointVariant>
                {newIPv6?.ip ? (
                  <Flex
                    sx={{
                      flexDirection: 'column',
                      textAlign: 'right',
                      overflowWrap: 'anywhere',
                      gap: '0.8rem',
                    }}
                  >
                    <TextWithOverFlowAndTippyPopup
                      theme={isLightMode ? 'light-org-tooltip' : 'org-tooltip'}
                      variant={['size12Weight400Line138', 'size15Weight400Line138']}
                      content={newIPv6.ip}
                      ariaLabel={newIPv6.ip}
                    />
                    <IpDescription
                      country={newIPv6?.country}
                      city={newIPv6?.city}
                      isp={newIPv6?.org}
                    />
                  </Flex>
                ) : (
                  <TextWithBreakpointVariant
                    variant={['size12Weight400Line138', 'size15Weight400Line138']}
                  >
                    N/A
                  </TextWithBreakpointVariant>
                )}
              </GroupItem>
            </Flex>
          )}
        </Group>
      )}
    </Group>
  )
}
